import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Head from 'next/head';
import Safe from 'react-safe';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import defaultMetadata from '../../../helpers/metadataHelper';
import { getCanonicalUrl } from '../../../helpers/seoHelper';
import productInfoDataSanitizer from '../../../components_common/ProductInfoCard/productInfoDataSanitizer';
import { generateAlternateURLs } from '../../../helpers/alternateURLs';
import FbOpenGraphMetadata from '../../../helpers/fbOpenGraphMetadata';

const mapStateToProps = ({
  api: {
    basePathAccount,
    basePathAdmin,
    basePathSecure,
    basePathWeb,
    brandName,
    categoryName,
    countries,
    country,
    countryId,
    currentSeasonFull,
    department,
    exclusions,
    fbOg,
    location,
    product,
    s3BasePath,
    yotpoKey,
  },
  countryCode,
}) => ({
  basePathAccount,
  basePathAdmin,
  basePathSecure,
  basePathWeb,
  brandName,
  categoryName,
  countries,
  country,
  countryCode,
  countryId,
  currentSeasonFull,
  department,
  exclusions,
  fbOg,
  location,
  product,
  s3BasePath,
  yotpoKey,
});

class HeadWrapper extends Component {
  getCanonicalUrl() {
    const {
      location: { hostname, path, query },
      product,
    } = this.props;

    if (!isEmpty(product)) {
      const { canonicalUrl } = product;
      if (canonicalUrl) {
        return `https://${hostname}${canonicalUrl}`;
      }
    }

    return getCanonicalUrl(hostname, path, query);
  }

  renderAlternateURLs = (countries, location, exclusions) => {
    return generateAlternateURLs(countries, location, exclusions).map(el => {
      return <link key={el.hrefLang} href={el.href} hrefLang={el.hrefLang} rel="alternate" />;
    });
  };

  render() {
    const {
      basePathWeb,
      countries,
      country,
      countryCode,
      exclusions,
      fbOg,
      location,
      metadata,
      product,
      s3BasePath,
      yotpoKey,
    } = this.props;
    const canonical = this.getCanonicalUrl();
    const propsSanitized = {
      ...this.props,
      product: productInfoDataSanitizer(product || {}),
    };

    const metadataTitle = get(metadata, 'title', defaultMetadata.main.title)(propsSanitized);
    const metadataDescription = get(metadata, 'description', defaultMetadata.main.description)(propsSanitized);

    return (
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0,  user-scalable=0" />
        <title>{metadataTitle}</title>
        {this.renderAlternateURLs(countries, location, exclusions, countryCode)}
        <meta name="description" content={metadataDescription} />
        <meta httpEquiv="content-type" content="text/html; charset=utf-8" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge,chrome=1" />
        <meta name="country" content={country} />
        {yotpoKey ? (
          <Safe.script name="yotpo" type="text/javascript">
            {`(function e(){
              var e = document.createElement("script");
              e.type="text/javascript";
              e.async=true;
              e.src="//staticw2.yotpo.com/{yotpoKey}/widget.js";
              var t=document.getElementsByTagName("script")[0];
              t.parentNode.insertBefore(e,t)
            })();`}
          </Safe.script>
        ) : null}
        <Safe.script async>
          {`
          (function () { 
            function loadKlaviyo() {
              setTimeout(function() {
                var klv = document.createElement('script');
                klv.src = 'https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=PqHk72';
                klv.async = true;
                document.getElementsByTagName('head')[0].appendChild(klv);
              }, 500);
            }
            loadKlaviyo();
          })();
        `}
        </Safe.script>
        {/* eslint-disable-next-line no-undef */}
        {PRODUCTION && (
          <>
            <Safe.script type="text/javascript">
              {`
                const _urconfig = { sid: "a5975b5e-4c16-4b93-b8e6-51751ee02379", aip: 0, usePageProtocol: false };
              `}
            </Safe.script>
            <Safe.script type="text/javascript">
              {`
                (function(w,d,t,r,u){var f,n,i;w[u]=w[u]||[],f=function(){var o={ti:"25032235"};
                o.q=w[u],w[u]=new UET(o),w[u].push("pageLoad")},n=d.createElement(t),n.src=r,n.async=1,
                n.onload=n.onreadystatechange=function(){var s=this.readyState;s&&s!=="loaded"&&s!=="complete"||
                (f(),n.onload=n.onreadystatechange=null)},i=d.getElementsByTagName(t)[0],
                i.parentNode.insertBefore(n,i)})(window,document,"script","//bat.bing.com/bat.js","uetq");
              `}
            </Safe.script>
            <meta name="msvalidate.01" content="9F4A590BE8C37963A7B3893776DFE82F" />
          </>
        )}
        <Safe.script>{`!function(){var analytics=window.analytics=window.analytics||[];if(!analytics.initialize)if(analytics.invoked)window.console&&console.error&&console.error("Segment snippet included twice.");else{analytics.invoked=!0;analytics.methods=["trackSubmit","trackClick","trackLink","trackForm","pageview","identify","reset","group","track","ready","alias","debug","page","once","off","on"];analytics.factory=function(t){return function(){var e=Array.prototype.slice.call(arguments);e.unshift(t);analytics.push(e);return analytics}};for(var t=0;t<analytics.methods.length;t++){var e=analytics.methods[t];analytics[e]=analytics.factory(e)}analytics.load=function(t){var e=document.createElement("script");e.type="text/javascript";e.async=!0;e.src=("https:"===document.location.protocol?"https://":"http://")+"cdn.segment.com/analytics.js/v1/"+t+"/analytics.min.js";var n=document.getElementsByTagName("script")[0];n.parentNode.insertBefore(e,n)};analytics.SNIPPET_VERSION="4.0.0";}}();`}</Safe.script>
        <meta name="language" content="en" />
        <FbOpenGraphMetadata
          pageDescription={metadataDescription}
          pageTitle={metadataTitle}
          s3BasePath={s3BasePath}
          canonicalUrl={canonical}
          fbOg={fbOg}
        />
        <meta name="p:domain_verify" content="2611d4b24275799f295fe3ff3512c921" />
        {canonical ? <link rel="canonical" href={canonical} /> : null}
        <link rel="image_src" href="static/images/logo_small.png'; ?>" />
        <link rel="shortcut icon" type="image/x-icon" href="/static/images/favicon.png" />
        <link rel="apple-touch-icon" href="/static/images/favicon_57.png" />
        <link rel="apple-touch-icon" sizes="72x72" href="/static/images/favicon_72.png" />
        <link rel="apple-touch-icon" sizes="114x114" href="/static/images/favicon_114.png" />
        {basePathWeb ? <base href={basePathWeb} /> : null}
      </Head>
    );
  }
}

HeadWrapper.propTypes = {
  basePathWeb: PropTypes.string.isRequired,
  basePathAdmin: PropTypes.string.isRequired,
  basePathSecure: PropTypes.string.isRequired,
  basePathAccount: PropTypes.string.isRequired,
  country: PropTypes.string.isRequired,
  countryId: PropTypes.string.isRequired,
  countries: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      isoCode: PropTypes.string.isRequired,
    }),
  ),
  currentSeasonFull: PropTypes.string,
  exclusions: PropTypes.arrayOf(PropTypes.string),
  fbOg: PropTypes.string,
  location: PropTypes.shape({
    hostname: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
    query: PropTypes.string,
  }).isRequired,
  product: PropTypes.object,
  yotpoKey: PropTypes.string,
  metadata: PropTypes.shape({
    title: PropTypes.func,
    description: PropTypes.func,
  }),
  countryCode: PropTypes.string,
  s3BasePath: PropTypes.string,
  urlQueryParams: PropTypes.object,
};

export default connect(mapStateToProps)(HeadWrapper);
